import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import AuthGate from "@/components/AuthGate";

const AuthGateContext = createContext(null);
const STORAGE_KEY = "t2c_pending_action";

/**
 * Serializable action shapes:
 *  { type: "external_url", url: "https://…" }
 *  { type: "start_chat", property_id: "prop_…" }
 *  { type: "reveal_phone" }           // no-op; UI re-renders once user is authed
 *  { type: "internal_link", path: "/…" }
 */
function runAction(action, navigate) {
  if (!action || !action.type) return;
  switch (action.type) {
    case "external_url":
      window.location.href = action.url;
      break;
    case "start_chat":
      api.post("/conversations", { property_id: action.property_id })
        .then(({ data }) => navigate(`/messages?c=${data.conversation_id}`))
        .catch(() => navigate("/messages"));
      break;
    case "internal_link":
      navigate(action.path);
      break;
    case "reveal_phone":
    default:
      break;
  }
}

export function AuthGateProvider({ children }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [contextInfo, setContextInfo] = useState(null); // { title, sub }
  const consumedRef = useRef(false);

  const requireAuth = useCallback((action, info = null) => {
    if (user) {
      runAction(action, navigate);
      return;
    }
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(action));
    setContextInfo(info);
    setOpen(true);
  }, [user, navigate]);

  const runPending = useCallback(() => {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (!stored) return false;
    sessionStorage.removeItem(STORAGE_KEY);
    try {
      runAction(JSON.parse(stored), navigate);
    } catch (err) {
      // corrupted storage
    }
    return true;
  }, [navigate]);

  // Auto-execute pending action when user becomes authenticated
  useEffect(() => {
    if (!user || consumedRef.current) return;
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (!stored) return;
    consumedRef.current = true;
    sessionStorage.removeItem(STORAGE_KEY);
    setOpen(false);
    try {
      runAction(JSON.parse(stored), navigate);
    } catch (err) {
      // corrupted storage
    }
  }, [user, navigate]);

  // Reset guard when user logs out (allow re-triggering)
  useEffect(() => {
    if (!user) consumedRef.current = false;
  }, [user]);

  return (
    <AuthGateContext.Provider value={{ requireAuth, runPending }}>
      {children}
      <AuthGate open={open} onOpenChange={setOpen} contextInfo={contextInfo} />
    </AuthGateContext.Provider>
  );
}

export function useAuthGate() {
  const ctx = useContext(AuthGateContext);
  if (!ctx) throw new Error("useAuthGate must be used within AuthGateProvider");
  return ctx;
}
